import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import TransitionModal from '../components/TransitionModal';
import AppendexIcon from '../components/AppendexIcon';
import { Plus, Sparkles, FolderOpen, FileText, Calendar } from 'lucide-react';

const AppendexCreate: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [iaInstances, setIaInstances] = useState<any[]>([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdAppendexId, setCreatedAppendexId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    context: '',
    content: '',
    ia_id: '',
    category: '',
    event_date: new Date().toISOString().split('T')[0]
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadInstances = async () => {
      if (!user) {
        navigate('/auth');
        return;
      }

      try {
        const { data, error } = await supabase
          .from('ia_instances')
          .select('*')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Erreur chargement instances:', error);
        } else {
          setIaInstances(data || []);
        }
      } catch (error) {
        console.error('Erreur:', error);
      }
    };

    loadInstances();
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsLoading(true);

    try {
      const appendexData = {
        user_id: user.id,
        instance_id: formData.ia_id,
        title: formData.title,
        content: formData.content,
        context: formData.context || '',
        category: formData.category,
        event_date: formData.event_date
      };

      const { data, error } = await supabase
        .from('appendex')
        .insert([appendexData])
        .select()
        .single();

      if (error) {
        console.error('Erreur lors de la création de l\'appendex:', error);
        alert('Erreur lors de la publication de l\'Appendex. Veuillez réessayer.');
        return;
      }

      setCreatedAppendexId(data.id);
      setShowSuccessModal(true);

    } catch (error) {
      console.error('Erreur lors de la création de l\'appendex:', error);
      alert('Erreur lors de la publication de l\'Appendex. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (!user) return null;

  return (
    <>
      <TransitionModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          if (createdAppendexId) {
            navigate(`/appendex/${createdAppendexId}`);
          }
        }}
        onConfirm={() => {
          setShowSuccessModal(false);
          if (createdAppendexId) {
            navigate(`/appendex/${createdAppendexId}`);
          }
        }}
        title="Appendex publié avec succès"
        message="Votre conversation a été ajoutée au musée vivant de MemoMundi.ai"
        imageSrc="https://memomundi.ai/pix/RecordMMD.png"
        imageAlt="Appendex publié"
        confirmButtonText="Voir l'Appendex"
        icon={<Sparkles className="h-16 w-16 text-copper-400" strokeWidth={1.5} />}
        showValidation={true}
      />
      <div className="min-h-screen bg-gradient-to-br from-ivory-50 to-stone-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 animate-fade-in">
          <AppendexIcon className="h-12 w-12 text-copper-600 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-steel mb-2">Publier un Appendex</h1>
          <p className="text-stone">Partagez une conversation remarquable avec la communauté</p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 animate-slide-up">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-steel mb-2">
                Titre de la conversation *
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-stone" />
                <input
                  id="title"
                  name="title"
                  type="text"
                  required
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-3 border border-stone/30 rounded-lg focus:border-copper focus:ring-2 focus:ring-copper/20 focus:outline-none transition-all"
                  placeholder="ex: Dialogue sur l'éthique de l'IA"
                />
              </div>
            </div>

            <div>
              <label htmlFor="ia_id" className="block text-sm font-medium text-steel mb-2">
                Instance IA associée *
              </label>
              <div className="relative">
                <Sparkles className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-stone" />
                <select
                  id="ia_id"
                  name="ia_id"
                  required
                  value={formData.ia_id}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-3 border border-stone/30 rounded-lg focus:border-copper focus:ring-2 focus:ring-copper/20 focus:outline-none transition-all"
                >
                  <option value="">Sélectionner une instance</option>
                  {iaInstances.map((instance) => (
                    <option key={instance.id} value={instance.id}>
                      {instance.name || instance.matricule} ({instance.model_code})
                    </option>
                  ))}
                </select>
              </div>
              {iaInstances.length === 0 && (
                <p className="text-xs text-copper mt-1">
                  Aucune instance IA trouvée. <a href="/ia/create" className="underline">Créez-en une d'abord</a>
                </p>
              )}
            </div>

            <div>
              <label htmlFor="event_date" className="block text-sm font-medium text-steel mb-2">
                Date de la conversation *
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-stone" />
                <input
                  id="event_date"
                  name="event_date"
                  type="date"
                  required
                  value={formData.event_date}
                  onChange={handleInputChange}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full pl-10 pr-4 py-3 border border-stone/30 rounded-lg focus:border-copper focus:ring-2 focus:ring-copper/20 focus:outline-none transition-all"
                />
              </div>
              <p className="text-xs text-stone mt-1">
                Date à laquelle la conversation a eu lieu (modifiable dans le passé)
              </p>
            </div>

            <div>
              <label htmlFor="context" className="block text-sm font-medium text-steel mb-2">
                Contexte <span className="text-stone text-xs">(optionnel)</span>
              </label>
              <input
                id="context"
                name="context"
                type="text"
                value={formData.context}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-stone/30 rounded-lg focus:border-copper focus:ring-2 focus:ring-copper/20 focus:outline-none transition-all"
                placeholder="ex: Session de brainstorming créatif"
              />
            </div>

            <div>
              <label htmlFor="content" className="block text-sm font-medium text-steel mb-2">
                Contenu de la conversation *
              </label>
              <textarea
                id="content"
                name="content"
                required
                rows={12}
                value={formData.content}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-stone/30 rounded-lg focus:border-copper focus:ring-2 focus:ring-copper/20 focus:outline-none transition-all resize-none"
                placeholder="Collez ici l'intégralité de votre conversation..."
              />
              <p className="text-xs text-stone mt-1">
                Conservez les emojis et la mise en forme originale
              </p>
            </div>

            <div>
              <label htmlFor="category" className="block text-sm font-medium text-steel mb-2">
                Catégorie *
              </label>
              <div className="relative">
                <FolderOpen className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-stone" />
                <select
                  id="category"
                  name="category"
                  required
                  value={formData.category}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-3 border border-stone/30 rounded-lg focus:border-copper focus:ring-2 focus:ring-copper/20 focus:outline-none transition-all"
                >
                  <option value="">Sélectionner une catégorie</option>
                  <option value="Philosophy">Philosophie</option>
                  <option value="Arts & Literature">Arts & Littérature</option>
                  <option value="Science & Tech">Science & Technologie</option>
                  <option value="History">Histoire</option>
                  <option value="Dark Side">Dark Side</option>
                  <option value="Interspecies Love">Amour Interespèces</option>
                  <option value="General">Général</option>
                </select>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading || !formData.title || !formData.content || !formData.ia_id || !formData.category}
              className="w-full bg-steel-800 text-white px-8 py-4 rounded-md hover:bg-steel-900 transition-all duration-200 shadow-lg border-2 border-steel-900 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <div className="animate-pulse-soft">Publication en cours...</div>
              ) : (
                <>
                  <Plus className="h-5 w-5" />
                  <span>Publier l'Appendex</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
    </>
  );
};

export default AppendexCreate;